/* eslint-disable prettier/prettier */
import React, { useEffect, useState, useContext } from 'react'
import {
    useNavigate,
    useParams,
    useSearchParams,
    useLoaderData,
    useRevalidator,
    useLocation,
} from 'react-router-dom'
import { useAuth } from '../../../../../hooks/auth'

import {
    Purchase,
    PurchaseLot,
    PurchaseLotItem,
    ContractPosition,
    CurrencyRate,
    ItemDetail,
    LongTermContractVolumes,
    ContractSupplier,
} from '../../types'

import { CurrencyList } from '../../../Plans/Positions/Edit/types'

import Footer from './components/Footer'

import { Structure } from '../../../Org/Structure/types'
import { RegionRf } from './types'

import { User } from '../../../Org/Users/types'
import { OrganizationInfo } from '../../../Org/Home/types'

import { ThemeContext } from '../../../../../components/ThemeContext'

import HoverTooltip from '../../../../../components/HoverTooltip'

import { CountryList } from '../types'

import { PurchaseMethod } from '../../../Org/PurchaseMethods/types'

import PurchaseStage from '../components/PurchaseStage'
import PurchaseInfo from '../components/PurchaseInfo'
import PurchaseTab from '../../components/Tabs/PurchaseTab'

import PurchaseChangeInfo from './components/PurchaseChangeInfo'
import PurchaseBaseInfo from './components/PurchaseBaseInfo'
import PurchaseContactInfo from './components/PurchaseContactInfo'
import PurchasePlannedDate from './components/PurchasePlannedDate'
import PurchaseApplSubmision from './components/PurchaseApplSubmision'
import PurchaseSummingup from './components/PurchaseSummingup'
import PurchaseDocumentationDelivery from './components/PurchaseDocumentationDelivery'
import PurchaseDocuments from './components/PurchaseDocuments'
import PurchaseLotTab from './components/PurchaseLotTab'

type ApiResponseRoleGuid = {
    result: QueryItem[]
}

type QueryItem = {
    guid: string
    name: string
    organization_guid: string
    created_at: string
    updated_at: string
}

const TABS = [
    'Извещение',
    'Лоты',
    // 'Документы',
]

export const loadPurchaseData = async ({
    params,
}: any): Promise<Purchase | null> => {
    const action = params.action
    const purchase_reg_number = params.purchase_reg_number
    const purchase_action = params.purchase_action
    const purchase_guid = params.purchase_guid

    // Для create — ничего не грузим
    if (!purchase_guid) return null

    const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/purchases/${purchase_guid}`

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
        // пусть отрисуется ErrorPage
        throw new Response('Failed to fetch purchase', {
            status: response.status,
        })
    }

    const data = await response.json()

    // API может вернуть либо объект, либо массив с одним объектом
    const purchase: Purchase | null = Array.isArray(data)
        ? (data[0] ?? null)
        : (data ?? null)

    // Доп. проверка, что это действительно контракт
    if (!purchase || !('guid' in purchase)) return null

    return purchase
}

const getStructureList = async (): Promise<Structure[]> => {
    return fetch(
        process.env.REACT_APP_API_URL + '/api/backend/v1/organization_struct',
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Ошибка запроса на способы закупок')
            }
        })
        .catch((e) => {
            console.error(e)
        })
}

const getRole = async (role_guid: string): Promise<QueryItem> => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/organizations/roles_by_guid/${role_guid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка запроса на роль')
        }

        const data: ApiResponseRoleGuid = await response.json()
        // Извлекаем первый элемент массива, т.к. он всегда содержит единственный объект
        return data.result[0]
    } catch (error) {
        console.error('Ошибка:', error)
        // Возвращаем объект по умолчанию, соответствующий типу QueryItem
        return {
            guid: '',
            name: '',
            organization_guid: '',
            created_at: '',
            updated_at: '',
        }
    }
}

const PurchaseEditPage: React.FC = () => {
    const loadedPurchase = useLoaderData() as Purchase

    const [purchase, setPurchase] = useState<Purchase>(loadedPurchase)

    const [orgData, setOrgData] = useState<OrganizationInfo>()

    const { user } = useAuth()
    const { theme } = useContext(ThemeContext)

    const { purchase_reg_number: purchase_reg_number } = useParams()
    const { purchase_guid: purchase_guid } = useParams()
    const { purchase_action: purchase_action } = useParams()

    const [isUserRoleName, setUserRoleName] = useState('')
    const [structList, setStructList] = useState<Structure[]>([])
    const [currencyList, setCurrencyList] = useState<CurrencyList[]>([])
    const [currencyRate, setCurrencyRate] = useState<CurrencyRate>()
    const [oksmList, setOksmList] = useState<CountryList[]>([])
    const [regionRfList, setRegionRfList] = useState<RegionRf[]>([])

    const [searchParams] = useSearchParams()
    const initialStage = searchParams.get('tab')

    const [tab, setTab] = useState(initialStage || '')

    const [isUserStructGuid, setUserStructGuid] = useState('')
    const [isUserStructName, setUserStructName] = useState('')

    const [purchasesLoading, setPurchasesLoading] = useState(false)
    const [purchasesError, setPurchasesError] = useState<string | null>(null)

    const [havePurchaseDocuments, setHavePurchaseDocuments] =
        useState<boolean>(false)

    // сбрасываем кеш при смене договора
    useEffect(() => {
        setPurchase(loadedPurchase ?? null)
        // тут же можно сбрасывать связанные состояния
    }, [loadedPurchase?.guid])

    const loadOrganizationData = async (): Promise<OrganizationInfo> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/organization',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then(async (response) => {
                if (response.ok) {
                    const data = await response.json()
                    setOrgData(data)
                    return data
                } else {
                    throw new Error('Failed to fetch organization')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getCurrencyList = async (): Promise<CurrencyList[]> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/currency',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на курсы валют')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getOksmList = async (): Promise<CountryList[]> => {
        return fetch(process.env.REACT_APP_API_URL + '/api/backend/v1/oksm', {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на Страны')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getRegionRfList = async (): Promise<RegionRf[]> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/region_rf',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на регионы закупок')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getCurrencyMeta = (
        currencyListData: CurrencyList[],
        digitalCode: number
    ): {
        currency_code: string
        currency_digital_code: number
        currency_name: string
    } | null => {
        const found = currencyListData.find(
            (item) => Number(item.num_code) === Number(digitalCode)
        )

        if (!found) return null

        return {
            currency_code: found.char_code,
            currency_digital_code: Number(found.num_code),
            currency_name: found.name,
        }
    }

    // useEffect(() => {
    //     if (purchase?.guid) {
    //         loadContractPositions(purchase.guid)
    //         loadContracSuppliers(purchase.guid)
    //     }
    // }, [purchase?.guid])

    useEffect(() => {
        const fetchData = async () => {
            try {
                const organization = await loadOrganizationData()
                if (organization) {
                    setOrgData(organization)
                }

                const role = await getRole(user?.object.role_id)
                setUserRoleName(role.name)

                const structs = await getStructureList()
                if (Array.isArray(structs)) {
                    setStructList(structs)
                    if (structs) {
                        const matchingStruct = structs.find(
                            (struct) =>
                                struct.guid.toLowerCase() ===
                                user?.object.struct_id
                        )

                        const structShortName =
                            matchingStruct?.struct_short_name || ''
                        setUserStructName(structShortName)
                        const structGuid = matchingStruct?.guid || ''
                        setUserStructGuid(structGuid)
                    }
                } else {
                    console.error('Нет данных:', structs)
                }

                const currency = await getCurrencyList()
                if (Array.isArray(currency)) {
                    setCurrencyList(currency)
                } else {
                    console.error('Нет данных:', currency)
                }

                const oksm = await getOksmList()
                if (Array.isArray(oksm)) {
                    setOksmList(oksm)
                } else {
                    console.error('Нет данных:', oksm)
                }

                const regionrf = await getRegionRfList()
                if (Array.isArray(regionrf)) {
                    setRegionRfList(regionrf)
                } else {
                    console.error('Нет данных:', regionrf)
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    const searchString = 'единствен'

    return (
        <div className=" flex flex-col w-full h-screen items-start justify-start ">
            <div className="flex flex-col w-full h-fit items-center justify-center">
                <div className="flex flex-col w-full h-[90px] items-center justify-center bg-[#e5e5e5] dark:bg-[#000000]">
                    <PurchaseStage purchase={purchase} theme={theme} />
                </div>
                <div className="flex flex-col w-full min-h-[100px] items-center justify-center bg-[#ebebeb] dark:bg-[#111111]">
                    <PurchaseInfo
                        purchase={purchase}
                        theme={theme}
                        currencyList={currencyList}
                    />
                </div>
                <div className="flex w-full h-[28px] items-center justify-center bg-[#ebebeb] dark:bg-[#111111]">
                    <PurchaseTab
                        tab={tab}
                        tabs={TABS}
                        onTabChange={(newTab) => {
                            setTab(newTab)
                        }}
                        theme={theme}
                    />
                </div>
            </div>

            <div className="grow flex flex-row w-full h-[calc(100vh_-_218px)] min-h-0 items-center justify-center gap-5 px-5 py-5 bg-[#f0f3f5] dark:bg-[#181818] ">
                <div className="flex flex-col min-w-[1270px] max-w-[1270px] h-full min-h-0 gap-y-12 items-start justify-start overflow-x-visible overflow-y-auto custom-scrollbar">
                    {tab === 'Извещение' && (
                        <fieldset
                            className={`flex flex-col w-full gap-10`}
                            disabled={false}
                        >
                            {purchase.version > 1 && (
                                <PurchaseChangeInfo
                                    purchaseAction={
                                        purchase_action == 'edit' ||
                                        purchase.purchase_stage ==
                                            'Отправка в ЕИС'
                                            ? true
                                            : false
                                    }
                                    purchaseGuid={purchase?.guid}
                                    theme={theme}
                                    purchasesLoading={purchasesLoading}
                                    purchasesError={purchasesError}
                                    purchase={purchase}
                                    onModificationDescription={(
                                        modification_description
                                    ) => {
                                        setPurchase((prev) => ({
                                            ...prev,
                                            modification_description:
                                                modification_description,
                                        }))
                                    }}
                                    onApproveDate={(change_decision_date) => {
                                        setPurchase((prev) => ({
                                            ...prev,
                                            change_decision_date:
                                                change_decision_date,
                                        }))
                                    }}
                                />
                            )}

                            <PurchaseBaseInfo
                                theme={theme}
                                purchaseAction={
                                    purchase_action == 'edit' ||
                                    purchase.purchase_stage == 'Отправка в ЕИС'
                                        ? true
                                        : false
                                }
                                purchaseGuid={purchase?.guid}
                                purchasesLoading={purchasesLoading}
                                purchasesError={purchasesError}
                                currencyData={currencyList}
                                purchase={purchase}
                                onEmergency={(emergency) => {
                                    setPurchase((prev) => ({
                                        ...prev,
                                        emergency: emergency,
                                    }))
                                }}
                                onNotDishonest={(not_dishonest) => {
                                    setPurchase((prev) => ({
                                        ...prev,
                                        not_dishonest: not_dishonest,
                                    }))
                                }}
                            />

                            <PurchaseContactInfo
                                theme={theme}
                                purchaseAction={
                                    purchase_action == 'edit' ||
                                    purchase.purchase_stage == 'Отправка в ЕИС'
                                        ? true
                                        : false
                                }
                                purchaseGuid={purchase?.guid}
                                purchasesLoading={purchasesLoading}
                                purchasesError={purchasesError}
                                purchase={purchase}
                            />

                            {!purchase.purchase_method_name
                                ?.toLowerCase()
                                .includes(searchString.toLowerCase()) && (
                                <>
                                    <PurchasePlannedDate
                                        theme={theme}
                                        purchaseAction={
                                            purchase_action == 'edit' ||
                                            purchase.purchase_stage ==
                                                'Отправка в ЕИС'
                                                ? true
                                                : false
                                        }
                                        purchaseGuid={purchase?.guid}
                                        purchasesLoading={purchasesLoading}
                                        purchasesError={purchasesError}
                                        purchase={purchase}
                                    />

                                    {purchase?.appl_submision_start_date ? (
                                        <PurchaseApplSubmision
                                            theme={theme}
                                            purchaseAction={
                                                purchase_action == 'edit' ||
                                                purchase.purchase_stage ==
                                                    'Отправка в ЕИС'
                                                    ? true
                                                    : false
                                            }
                                            purchaseGuid={purchase?.guid}
                                            purchasesLoading={purchasesLoading}
                                            purchasesError={purchasesError}
                                            purchase={purchase}
                                        />
                                    ) : (
                                        ''
                                    )}

                                    <PurchaseSummingup
                                        theme={theme}
                                        purchaseAction={
                                            purchase_action == 'edit' ||
                                            purchase.purchase_stage ==
                                                'Отправка в ЕИС'
                                                ? true
                                                : false
                                        }
                                        purchaseGuid={purchase?.guid}
                                        purchasesLoading={purchasesLoading}
                                        purchasesError={purchasesError}
                                        purchase={purchase}
                                    />
                                </>
                            )}

                            <PurchaseDocumentationDelivery
                                theme={theme}
                                purchaseAction={
                                    purchase_action == 'edit' ||
                                    purchase.purchase_stage == 'Отправка в ЕИС'
                                        ? true
                                        : false
                                }
                                purchaseGuid={purchase?.guid}
                                purchasesLoading={purchasesLoading}
                                purchasesError={purchasesError}
                                purchase={purchase}
                                currencyData={currencyList}
                                onPayment={(payment) => {
                                    if (payment == false) {
                                        setPurchase((prev) => ({
                                            ...prev,
                                            payment: null,
                                        }))
                                    } else {
                                        setPurchase((prev) => ({
                                            ...prev,
                                            payment: {
                                                currency: {
                                                    code: 'RUB',
                                                    name: 'Российский рубль',
                                                    digital_code: '643',
                                                },
                                                sum: 0,
                                                procedure: '',
                                            },
                                        }))
                                    }
                                }}
                                onCurrencyChange={(value) => {
                                    const currencyMeta = getCurrencyMeta(
                                        currencyList,
                                        value
                                    )
                                    setPurchase((prev) => ({
                                        ...prev,
                                        payment: prev.payment && {
                                            ...prev.payment,
                                            currency: {
                                                code:
                                                    currencyMeta?.currency_code ??
                                                    'RUB',
                                                name:
                                                    currencyMeta?.currency_name ??
                                                    'Российский рубль',
                                                digital_code:
                                                    currencyMeta?.currency_digital_code ??
                                                    0,
                                            },
                                        },
                                    }))
                                }}
                                onPaymentSum={(value) => {
                                    setPurchase((prev) => ({
                                        ...prev,
                                        payment: prev.payment && {
                                            ...prev.payment,
                                            sum: value,
                                        },
                                    }))
                                }}
                            />

                            <PurchaseDocuments
                                theme={theme}
                                purchaseAction={
                                    purchase_action == 'edit' ||
                                    purchase.purchase_stage == 'Отправка в ЕИС'
                                        ? true
                                        : false
                                }
                                purchaseGuid={purchase?.guid}
                                purchase={purchase}
                                role={isUserRoleName}
                                onPurchaseDocuments={(
                                    have_purchase_documents
                                ) =>
                                    setHavePurchaseDocuments(
                                        have_purchase_documents
                                    )
                                }
                            />
                        </fieldset>
                    )}
                    {tab === 'Лоты' && (
                        <fieldset
                            className={`flex flex-col w-full`}
                            disabled={false}
                        >
                            <PurchaseLotTab
                                purchaseGuid={purchase.guid}
                                purchaseRegNumber={String(
                                    purchase.registration_number
                                )}
                                purchaseAction={
                                    purchase_action == 'edit' ||
                                    purchase.purchase_stage == 'Отправка в ЕИС'
                                        ? true
                                        : false
                                }
                                theme={theme}
                                isActive={tab === 'Лоты'}
                                currencyList={currencyList}
                            />
                        </fieldset>
                    )}
                </div>
            </div>

            <Footer
                theme={theme}
                purchaseAction={purchase_action == 'edit' ? true : false}
                purchaseGuid={purchase_guid}
                purchase={purchase}
                user_guid={user?.object.user_id}
                contract_user_guid={purchase.user_guid}
                contract_supervisor_user_guid={purchase.supervisor_user_guid}
                role={isUserRoleName}
                stage={purchase.purchase_stage}
                status={purchase.purchase_status}
            />
        </div>
    )
}

export default PurchaseEditPage
